import React, { useEffect, useRef } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { TrackReference, VideoTrack } from '@livekit/components-react';

import { DS } from '../designTokens';

export const thumbnailSx = {
  flex: 'none',
  width: 80,
  height: 128,
  borderRadius: '12px',
  border: `2px solid ${DS.selfBorder}`,
  bgcolor: DS.selfTile,
};

const PersonPlaceholder = () => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: '50%',
      bgcolor: DS.surfaceTint,
      display: 'grid',
      placeItems: 'center',
    }}
  >
    <PersonIcon sx={{ fontSize: 22, color: DS.softBlack }} />
  </Box>
);

type TileVideoProps = {
  track: TrackReference;
  mirrored?: boolean;
  onAspectRatio?: (aspectRatio: number) => void;
};

const TileVideo = ({ track, mirrored, onAspectRatio }: TileVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  // A phone that rotates mid-call changes the size without new metadata.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !onAspectRatio) return undefined;

    const measure = () => {
      if (video.videoWidth && video.videoHeight) {
        onAspectRatio(video.videoWidth / video.videoHeight);
      }
    };
    measure();
    video.addEventListener('loadedmetadata', measure);
    video.addEventListener('resize', measure);
    return () => {
      video.removeEventListener('loadedmetadata', measure);
      video.removeEventListener('resize', measure);
    };
  }, [track, onAspectRatio]);

  return (
    <VideoTrack
      ref={videoRef}
      trackRef={track}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transform: mirrored ? 'scaleX(-1)' : undefined,
      }}
    />
  );
};

type Props = {
  track?: TrackReference;
  showVideo: boolean;
  label?: string;
  badge?: React.ReactNode;
  mirrored?: boolean;
  placeholder?: React.ReactNode;
  onAspectRatio?: (aspectRatio: number) => void;
  sx?: SxProps<Theme>;
};

const VideoTile = ({
  track,
  showVideo,
  label,
  badge,
  mirrored,
  placeholder = <PersonPlaceholder />,
  onAspectRatio,
  sx = [],
}: Props) => (
  <Box
    sx={[
      {
        position: 'relative',
        overflow: 'hidden',
        display: 'grid',
        placeItems: 'center',
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    {track && showVideo ? (
      <TileVideo
        track={track}
        mirrored={mirrored}
        onAspectRatio={onAspectRatio}
      />
    ) : (
      placeholder
    )}
    {label && (
      <>
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 36,
            background: DS.labelScrim,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: 8,
            right: 6,
            bottom: 6,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontFamily: DS.fontBody,
            fontSize: 10,
            fontWeight: 700,
            color: DS.onDarkLabel,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {badge}
          {label}
        </Box>
      </>
    )}
  </Box>
);

export default VideoTile;
